import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import type { ParentRole } from "@starquest/core";
import styles from "./ParentLogin.module.css";

type Step = "email" | "code";

export default function ParentLogin() {
  const navigate = useNavigate();
  const setAuth  = useAuthStore((s) => s.setAuth);

  const rememberedEmail = localStorage.getItem("sq_remembered_email") ?? "";

  const [step,    setStep]    = useState<Step>("email");
  const [email,   setEmail]   = useState(rememberedEmail);
  const [code,    setCode]    = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.sendOtp(email.trim().toLowerCase());
      setStep("code");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to send code");
    } finally { setLoading(false); }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await api.verifyOtp(email.trim().toLowerCase(), code.trim());
      localStorage.setItem("sq_token", res.token);
      // Remember this email for next login (survives logout)
      localStorage.setItem("sq_remembered_email", email.trim().toLowerCase());

      if (res.needsSetup) {
        // Store email in authStore now so Setup.tsx can read it correctly.
        // familyId is empty — Setup will update it after creating the family.
        setAuth(res.token, "", email.trim().toLowerCase(), "admin");
        navigate("/setup");
      } else {
        const payload = JSON.parse(atob(res.token.split(".")[1]));
        setAuth(res.token, res.familyId!, email.trim().toLowerCase(), payload.parentRole as ParentRole);
        navigate("/parent");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Invalid code");
    } finally { setLoading(false); }
  }

  function switchAccount() {
    localStorage.removeItem("sq_remembered_email");
    setEmail("");
    setStep("email");
    setCode("");
    setError("");
  }

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => navigate("/")}>← Back</button>

      <div className={styles.card}>
        <div className={styles.icon}>⭐</div>
        <h2 className={styles.title}>Quest Master Login</h2>

        {step === "email" ? (
          <form onSubmit={handleSendCode} className={styles.form}>
            {rememberedEmail ? (
              /* Remembered email — show as chip, one click to send code */
              <>
                <p className={styles.sub}>Welcome back! We'll send a code to:</p>
                <div className={styles.rememberedChip}>
                  <span className={styles.rememberedEmail}>{email}</span>
                  <button type="button" className={styles.switchBtn} onClick={switchAccount}>
                    Change
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className={styles.sub}>Enter your email to receive a 6-digit code.</p>
                <input
                  className={styles.input}
                  type="email" placeholder="parent@email.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  required autoFocus
                />
              </>
            )}
            {error && <p className={styles.error}>{error}</p>}
            <button className={styles.btn} disabled={loading || !email.trim()}>
              {loading ? "Sending…" : "Send Code →"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className={styles.form}>
            <p className={styles.sub}>Enter the 6-digit code sent to <strong>{email}</strong>.</p>
            <input
              className={`${styles.input} ${styles.codeInput}`}
              type="text" inputMode="numeric" pattern="[0-9]{6}"
              placeholder="000000" maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              required autoFocus
            />
            {error && <p className={styles.error}>{error}</p>}
            <button className={styles.btn} disabled={loading || code.length < 6}>
              {loading ? "Verifying…" : "Enter Command Center"}
            </button>
            <button type="button" className={styles.resend} onClick={() => { setStep("email"); setCode(""); setError(""); }}>
              Use a different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
